import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema } from '../schemas';
import { userPreferencesSchema } from '../../interfaces/IUserPreferences';

// ============================================================
// Params Schemas
// ============================================================

const usernameParamsSchema = z.object({
	username: requiredStringSchema,
});

// ============================================================
// Body Schemas
// ============================================================

/**
 * SELF scope: the server takes the username from the token, so the client never
 * sends one. Omitting it from the schema is what makes writing somebody else's
 * preferences inexpressible rather than merely checked.
 */
const ownPreferencesBodySchema = userPreferencesSchema.omit({ _id: true, username: true });

// ============================================================
// Contract
// ============================================================

/**
 * `{ root: true }` because the two scopes in this file want different URI
 * shapes and neither is `/userPreferences/...`:
 *
 * - SELF scope must have NO identity in the URI at all, so the caller's own
 *   preferences stay at a bare `/userPreferences`.
 * - ADMINISTERED-USER scope is `/users/:username/…` — a sub-resource of the user
 *   being administered — which cannot sit behind a `userPreferences` prefix.
 *
 * So every route spells its own full path, and the table is pinned in
 * `Entities/src/tests/unit/fastify/userRouteTable.test.ts`.
 */
export const userPreferencesContract = defineContract(
	'userPreferences',
	{
		getOwnUserPreferences: route({
			method: 'GET',
			path: '/userPreferences',
			response: userPreferencesSchema.nullable(),
			operationId: 'getOwnUserPreferences',
			tags: ['UserPreferences'],
			summary: "Get the caller's own preferences",
		}),
		updateOwnUserPreferences: route({
			method: 'PUT',
			path: '/userPreferences',
			body: ownPreferencesBodySchema.partial(),
			response: userPreferencesSchema,
			operationId: 'updateOwnUserPreferences',
			tags: ['UserPreferences'],
			summary: "Update the caller's own preferences",
		}),
		/**
		 * ADMINISTERED-USER scope — the one legitimate cross-user read: an admin
		 * editing a user needs their current language. Role-gated, and the target
		 * must share an organization with the caller.
		 *
		 * D8: `/users/:username/preferences` rather than
		 * `/userPreferences/:username`, so every administered-user route in the
		 * service has the same shape — the four on `user.contract.ts` and this one.
		 * A preference sheet is a sub-resource of the user it belongs to.
		 */
		getUserPreferencesByUsername: route({
			method: 'GET',
			path: '/users/:username/preferences',
			params: usernameParamsSchema,
			response: userPreferencesSchema.nullable(),
			operationId: 'getUserPreferencesByUsername',
			tags: ['UserPreferences'],
			summary: "Get an administered user's preferences",
		}),
	},
	{ root: true }
);
